// UX80 (docs/build-briefs/UX80-operations-menu.md; DEC-298): which permission
// ids open which Settings group, and which of them put a Settings row in the
// account menu at all. Pure data with no DOM, so a plain node:test file can
// import it (test/account-menu-model.test.ts) and ui/src/admin.ts reads the
// very same table, never a copy.
//
// Before UX80 these unions had been widened (UX18-F1, PO6-R1, RPT1) so that
// people who do day to day work could still reach their screen THROUGH
// Settings. The operational screens now live in the account menu, so every
// group below is back to configuration permissions only. A group's visibility
// inside Settings can still be granted by a view-only id (People, Vendors), but
// a view-only id alone never puts a Settings row in the menu (DEC-298 point 6).

export type AdminSection =
  | "money"
  | "tabsPayment"
  | "discounts"
  | "corrections"
  | "recipes"
  | "menu"
  | "inventory"
  | "purchasing"
  | "people"
  | "operations"
  | "tablets"
  | "auditLog"
  | "network"
  | "connections";

/** The rail's own top-to-bottom order. The labels in the markup are
 * Money and pricing, Tabs and payment, Recipes, Menu, Inventory items,
 * Vendors, Discounts, Reasons, People and access, Location, Devices (SEC2-S3,
 * DEC-321: after Location), Audit log (RPTLAYOUT1-F1-S2), Network and billing (UX80 renames: Inventory to Inventory items, Purchasing to
 * Vendors, Corrections to Reasons, Operations to Location). */
export const ADMIN_SECTION_ORDER: readonly AdminSection[] = [
  "inventory", "purchasing", "recipes", "people", "discounts", "corrections",
  "money", "tabsPayment", "tablets", "operations", "connections", "auditLog", "network",
];

export const ADMIN_TAB_PERMISSION_IDS: Record<AdminSection, readonly string[]> = {
  money: ["admin.tenant_settings.view"],
  tabsPayment: ["admin.tenant_settings.view"],
  discounts: ["admin.tenant_settings.view"],
  // Renamed Reasons (labels only; the key and test id are unchanged).
  corrections: ["admin.tenant_settings.view"],
  recipes: ["admin.menu_drink.manage"],
  menu: ["admin.menu_drink.manage"],
  // Inventory items: the item catalog and the count areas. Counting itself
  // (inv.count_session.run) and the count variance view left with the Counts
  // screen (UX80), and so did on hand and movements (Stock levels).
  inventory: ["inv.vendor_offer.manage", "admin.liquor_product.view", "admin.liquor_product.manage", "inv.count_section.manage", "admin.menu_drink.manage", "admin.location_settings.manage"],
  // Vendors. inv.vendor.view keeps a read only vendor list reachable for
  // someone who reaches Settings for another reason (an inventory manager
  // holds it beside inv.vendor.manage); the ordering permissions
  // (purchasing.order.*, purchasing.cart.manage, inv.receipt.*) left with the
  // Ordering screens.
  purchasing: ["inv.vendor.manage", "inv.vendor.view", "purchasing.approval_threshold.manage"],
  people: ["admin.user.view"],
  // Renamed Location. guest.reconciliation.view and admin.mytime_sync.view
  // left with Match clients.
  // UX128: Locations are set up here, so a session that may add a location (admin.location.manage) reaches the group.
  operations: ["admin.tenant_settings.view", "admin.location.manage", "admin.location_permits.view", "admin.location_permits.manage"],
  // SEC2-S3 (DEC-320 ruling 3): Admin and Manager add and revoke tablets.
  // UX126: Card readers are listed here too, so a session that may view them (admin.terminal.view) reaches the group.
  // UX127: Receipt printers are listed here too; printer.manage is the one permission every printer route requires.
  tablets: ["admin.device.manage", "admin.terminal.view", "printer.manage"],
  network: ["admin.tenant_settings.view", "billing.ai.manage"],
  // ACCTEXPORT1 and PAYEXPORT1 (DEC-514, owner 2026-10-22): a location's outside connections, QuickBooks and Gusto. The rail item shows
  // only when at least one of the two features is on (admin.ts decides from what each card's own read answers).
  connections: ["admin.tenant_settings.view", "payroll.gusto.manage", "payroll.tips.send"],
  // RPTLAYOUT1-F1-S2: the Audit log moved here from the Reports hub (it is a record of changes, not a report). Same permission.
  auditLog: ["admin.change_history.view"],
};

/** The flat union of every group's own gating permission(s). */
export const ADMIN_TAB_PERMISSIONS: readonly string[] = Array.from(
  new Set(Object.values(ADMIN_TAB_PERMISSION_IDS).flat()),
);

/** Ids that may show a group to someone who is already in Settings but never
 * open Settings on their own: a read only list is not something to configure
 * (DEC-298 point 6: "never for view-only users or vendors alone"). */
export const VIEW_ONLY_SETTINGS_PERMISSIONS: readonly string[] = [
  "admin.liquor_product.view",
  "admin.user.view",
  "inv.vendor.view",
  "admin.location_permits.view",
];

/** The Settings row in the account menu shows for a person holding any one of
 * these: at least one settings manage permission, or the general settings view
 * (admin.tenant_settings.view). Derived from the tab table so the two cannot
 * drift: a Settings row always opens at least one group. */
export const SETTINGS_ENTRY_PERMISSIONS: readonly string[] = ADMIN_TAB_PERMISSIONS.filter(
  (id) => !VIEW_ONLY_SETTINGS_PERMISSIONS.includes(id),
);
